import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  AlertTitle,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Switch,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import StorageIcon from '@mui/icons-material/Storage';
import swal from 'sweetalert';
import { useTranslation } from 'react-i18next';
import Colors from '../../components/ui/Colors';
import SchedulePicker from './SchedulePicker';
import {
  CLOUDS,
  createConnection,
  issueTemplate,
  getConnection,
  validateConnection,
  verifyTrust,
  updateDataOptions,
  updateSchedule,
  stateChipColor,
  wizardStepForState,
  formatSnapshotToken,
} from './federationApi';

const POLL_MS = 3000;
// States at or beyond TRUST_VERIFIED — the external callback has bound the principal,
// so the wizard can stop polling and let the user validate.
const TRUST_REACHED = ['TRUST_VERIFIED', 'VALIDATED', 'SCHEDULED', 'ACTIVE', 'DEGRADED'];

/**
 * Multi-step onboarding wizard for a cross-cloud federation connection, walking
 * DRAFT → ACTIVE. NO cloud credentials are handled here: step 2 renders the IaC
 * (CloudFormation / Terraform / Azure script or ARM) the client runs in THEIR cloud;
 * on apply it calls our callback with a single-use onboarding token and the browser
 * only polls connection state. If the deployment cannot reach us, the "verify trust
 * manually" form binds the principal the client reports (FEDERATION_MANAGE).
 *
 * Props:
 *  - jwt:          bearer token.
 *  - connection:   optional existing ConnectionDetailDto to resume / re-onboard.
 *  - onCompleted:  (connectionId) => void — schedule saved (SCHEDULED/ACTIVE path).
 *  - onConnectionChanged: (connection) => void — bubbles state so the parent list refreshes.
 */
export default function FederationOnboardingWizard({ jwt, connection: initialConnection, onCompleted, onConnectionChanged }) {
  const { t } = useTranslation();

  const [connection, setConnection] = React.useState(initialConnection || null);
  const [activeStep, setActiveStep] = React.useState(
    initialConnection ? wizardStepForState(initialConnection.onboardingState) : 0,
  );

  // Step 1 — source form
  const [form, setForm] = React.useState({
    cloud: initialConnection?.cloud || 'AWS',
    accountOrProjectId: initialConnection?.accountOrProjectId || '',
    bucketOrContainer: initialConnection?.bucketOrContainer || '',
    prefix: initialConnection?.prefix || '',
    audience: '',
    entraIdentityRef: '',
    gcpProjectNumber: '',
  });
  // Manual trust verification (operator fallback) form.
  const [manual, setManual] = React.useState({ open: false, principal: '', projectNumber: '' });

  const [template, setTemplate] = React.useState(null);
  const [validation, setValidation] = React.useState(null);
  // Data options chosen on the validate step (initialized from the first validation result).
  const [dataOptions, setDataOptions] = React.useState(null);
  const [optionsApplied, setOptionsApplied] = React.useState(false);
  // True once the user flips the CSV-header switch in this session: from then on the
  // backend's suggestedCsvHeader no longer overrides their choice.
  const csvHeaderTouchedRef = React.useRef(false);
  // True while the switch value comes from suggestedCsvHeader (not yet touched).
  const [csvHeaderFromSuggestion, setCsvHeaderFromSuggestion] = React.useState(false);
  const [scheduleResult, setScheduleResult] = React.useState(null);

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const pollRef = React.useRef(null);

  const propagate = React.useCallback(
    (conn) => {
      setConnection(conn);
      if (conn && onConnectionChanged) onConnectionChanged(conn);
    },
    [onConnectionChanged],
  );

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  React.useEffect(() => () => stopPolling(), [stopPolling]);

  const trustReached = connection && TRUST_REACHED.includes(connection.onboardingState);
  // Outstanding callback token expiry (fresh template first, else the stored one).
  const tokenExpiresAt = template?.callbackTokenExpiresAt || connection?.callbackTokenExpiresAt || null;
  // GCP bound without a project number: validation would fail — offer the fallback.
  const gcpNumberMissing =
    !!trustReached &&
    connection?.cloud === 'GCP' &&
    !connection?.gcpProjectNumber &&
    !connection?.gcpWorkloadIdentityProvider;

  // ── Step 1: create DRAFT ────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.cloud) {
      setError(t('Federation.wizard.errors.cloudRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    const request = {
      cloud: form.cloud,
      accountOrProjectId: form.accountOrProjectId || null,
      bucketOrContainer: form.bucketOrContainer || null,
      prefix: form.prefix || null,
      audience: form.audience || null,
      entraIdentityRef: form.entraIdentityRef || null,
      gcpProjectNumber: form.cloud === 'GCP' && form.gcpProjectNumber.trim() ? form.gcpProjectNumber.trim() : null,
      // readMode omitted: the backend defaults it (COPY_INTO_DELTA is the only mode).
    };
    const res = await createConnection(request, jwt);
    setBusy(false);
    if (!res.ok || !res.data || !res.data.id) {
      // A 400 carries the reason (e.g. Azure storage in another Entra tenant).
      setError(
        res.error
          ? t('Federation.wizard.errors.createFailedWithReason', { message: res.error })
          : t('Federation.wizard.errors.createFailed'),
      );
      return;
    }
    propagate(res.data);
    setActiveStep(1);
  };

  // ── Step 2: issue template + poll for TRUST_VERIFIED ────────────────────────
  const handleIssueTemplate = React.useCallback(async () => {
    if (!connection?.id) return;
    setBusy(true);
    setError(null);
    const tpl = await issueTemplate(connection.id, jwt);
    setBusy(false);
    if (!tpl) {
      setError(t('Federation.wizard.errors.templateFailed'));
      return;
    }
    setTemplate(tpl);
    // Reflect the TEMPLATE_ISSUED advance without a full re-fetch.
    const refreshed = await getConnection(connection.id, jwt);
    if (refreshed) propagate(refreshed);
  }, [connection, jwt, t, propagate]);

  // Auto-issue the template only for a fresh DRAFT. Every issue ROTATES the single-use
  // callback token, so resuming a TEMPLATE_ISSUED connection must not silently invalidate
  // a template the client may already be running — the user regenerates explicitly.
  React.useEffect(() => {
    if (activeStep === 1 && connection?.id && !template && connection.onboardingState === 'DRAFT') {
      handleIssueTemplate();
    }
  }, [activeStep, connection, template, handleIssueTemplate]);

  // Regenerate: confirm first when a template (token) is already outstanding.
  const handleRegenerateTemplate = async () => {
    if (connection?.onboardingState === 'TEMPLATE_ISSUED') {
      const ok = await swal({
        title: t('Federation.wizard.regenerateConfirmTitle'),
        text: t('Federation.wizard.regenerateConfirmBody'),
        icon: 'warning',
        buttons: [t('Federation.wizard.cancel'), t('Federation.wizard.regenerate')],
        dangerMode: true,
      });
      if (!ok) return;
    }
    handleIssueTemplate();
  };

  // ── Step 2 fallback: bind trust by hand ─────────────────────────────────────
  const handleManualVerify = async () => {
    if (!connection?.id) return;
    const cloud = connection.cloud;
    const principal = manual.principal.trim();
    const projectNumber = manual.projectNumber.trim();
    if (!principal) {
      setError(t('Federation.wizard.manualVerify.principalRequired'));
      return;
    }
    const body = { projectNumber: cloud === 'GCP' && projectNumber ? projectNumber : null };
    if (cloud === 'AWS') body.roleArn = principal;
    else if (cloud === 'GCP') body.serviceAccount = principal;
    else body.principal = principal;
    setBusy(true);
    setError(null);
    const res = await verifyTrust(connection.id, body, jwt);
    setBusy(false);
    if (!res.ok || !res.data) {
      setError(
        res.error
          ? t('Federation.wizard.errors.verifyFailedWithReason', { message: res.error })
          : t('Federation.wizard.errors.verifyFailed'),
      );
      return;
    }
    propagate(res.data);
    setManual({ open: false, principal: '', projectNumber: '' });
  };

  // Poll GET /connections/{id} on step 2 until the external callback binds trust.
  React.useEffect(() => {
    stopPolling();
    if (activeStep !== 1 || !connection?.id || trustReached) return undefined;
    pollRef.current = setInterval(async () => {
      const refreshed = await getConnection(connection.id, jwt);
      if (refreshed) {
        propagate(refreshed);
        if (TRUST_REACHED.includes(refreshed.onboardingState)) stopPolling();
      }
    }, POLL_MS);
    return () => stopPolling();
  }, [activeStep, connection, trustReached, jwt, stopPolling, propagate]);

  // ── Step 3: validate (+ data options) ───────────────────────────────────────
  const handleValidate = async () => {
    if (!connection?.id) return false;
    setBusy(true);
    setError(null);
    setOptionsApplied(false);
    const res = await validateConnection(connection.id, jwt);
    setBusy(false);
    if (!res.ok) {
      // A 409 names the unreadable / wrong-format object; otherwise the generic text.
      setError(
        res.error
          ? t('Federation.wizard.errors.validateFailedWithReason', { message: res.error })
          : t('Federation.wizard.errors.validateFailed'),
      );
      return false;
    }
    const result = res.data;
    setValidation(result);
    // Backend verdict from the CSV first lines (null = no CSV tables or tables disagree).
    const suggested = typeof result.suggestedCsvHeader === 'boolean' ? result.suggestedCsvHeader : null;
    const applySuggestion = suggested !== null && !csvHeaderTouchedRef.current;
    // First result of this session seeds the data options: a dated layout defaults to
    // LATEST (full-snapshot exports are the common case); otherwise keep the stored mode.
    // The CSV-header switch follows suggestedCsvHeader until the user flips it.
    setDataOptions((prev) => {
      const seeded = prev || {
        snapshotMode: result.datedLayoutDetected ? 'LATEST' : connection.snapshotMode || 'ALL',
        csvHeader: connection.csvHeader ?? true,
      };
      return applySuggestion ? { ...seeded, csvHeader: suggested } : seeded;
    });
    if (applySuggestion) setCsvHeaderFromSuggestion(true);
    const refreshed = await getConnection(connection.id, jwt);
    if (refreshed) propagate(refreshed);
    return true;
  };

  const showSnapshotOption = !!validation?.datedLayoutDetected;
  const showCsvHeaderOption = (validation?.tables || []).some((tbl) => tbl.format === 'CSV');
  const storedSnapshotMode = connection?.snapshotMode || 'ALL';
  const storedCsvHeader = connection?.csvHeader ?? true;
  const snapshotModeChanged = showSnapshotOption && !!dataOptions && dataOptions.snapshotMode !== storedSnapshotMode;
  const csvHeaderChanged = showCsvHeaderOption && !!dataOptions && dataOptions.csvHeader !== storedCsvHeader;

  // Continue from the validate step. Changed data options are saved first; a changed
  // snapshot mode alters which files are selected (supportedObjectCount), so validation is
  // re-run and the user stays on this step to review the refreshed results.
  const handleContinueFromValidate = async () => {
    if (!connection?.id) return;
    if (!snapshotModeChanged && !csvHeaderChanged) {
      setActiveStep(3);
      return;
    }
    setBusy(true);
    setError(null);
    const saved = await updateDataOptions(
      connection.id,
      {
        snapshotMode: snapshotModeChanged ? dataOptions.snapshotMode : null,
        csvHeader: csvHeaderChanged ? dataOptions.csvHeader : null,
      },
      jwt,
    );
    setBusy(false);
    if (!saved) {
      setError(t('Federation.wizard.errors.dataOptionsFailed'));
      return;
    }
    propagate(saved);
    if (snapshotModeChanged) {
      if (await handleValidate()) setOptionsApplied(true);
      return;
    }
    setActiveStep(3);
  };

  // ── Step 4: save schedule (enqueues ACTIVATION) ─────────────────────────────
  const handleSaveSchedule = async (spec) => {
    if (!connection?.id) return;
    setBusy(true);
    setError(null);
    const saved = await updateSchedule(connection.id, spec, jwt);
    setBusy(false);
    if (!saved) {
      setError(t('Federation.wizard.errors.scheduleFailed'));
      return;
    }
    setScheduleResult(saved);
    const refreshed = await getConnection(connection.id, jwt);
    if (refreshed) propagate(refreshed);
    swal({ title: t('Federation.wizard.scheduledTitle'), text: t('Federation.wizard.scheduledBody'), icon: 'success' });
    if (onCompleted) onCompleted(connection.id);
  };

  const download = (filename, text) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const formatInstant = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      swal({ title: t('Federation.wizard.copied'), icon: 'success', timer: 1200, buttons: false });
    } catch (e) {
      setError(t('Federation.wizard.errors.copyFailed'));
    }
  };

  const accountLabel = t(`Federation.wizard.accountLabel.${form.cloud}`);

  const steps = [
    t('Federation.wizard.steps.source'),
    t('Federation.wizard.steps.template'),
    t('Federation.wizard.steps.validate'),
    t('Federation.wizard.steps.schedule'),
  ];

  const renderCopyRow = (label, value) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
          {value}
        </Typography>
      </Box>
      <Tooltip title={t('Federation.wizard.copy')}>
        <IconButton size="small" onClick={() => copy(value)}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ── Renderers ────────────────────────────────────────────────────────────────
  const renderSourceStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <TextField
        select
        label={t('Federation.wizard.cloud')}
        value={form.cloud}
        onChange={(e) => setForm((f) => ({ ...f, cloud: e.target.value }))}
        disabled={!!connection}
        fullWidth
      >
        {CLOUDS.map((c) => (
          <MenuItem key={c} value={c}>
            {t(`Federation.cloud.${c}`)}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label={accountLabel}
        value={form.accountOrProjectId}
        onChange={(e) => setForm((f) => ({ ...f, accountOrProjectId: e.target.value }))}
        helperText={form.cloud === 'AZURE' ? t('Federation.wizard.accountLabel.AZURE_help') : undefined}
        fullWidth
      />

      <TextField
        label={t(`Federation.wizard.bucketLabel.${form.cloud}`)}
        value={form.bucketOrContainer}
        onChange={(e) => setForm((f) => ({ ...f, bucketOrContainer: e.target.value }))}
        fullWidth
      />

      <TextField
        label={t('Federation.wizard.prefix')}
        value={form.prefix}
        onChange={(e) => setForm((f) => ({ ...f, prefix: e.target.value }))}
        helperText={t('Federation.wizard.prefixHelp')}
        fullWidth
      />

      {form.cloud === 'GCP' && (
        <TextField
          label={t('Federation.wizard.gcpProjectNumber')}
          value={form.gcpProjectNumber}
          onChange={(e) => setForm((f) => ({ ...f, gcpProjectNumber: e.target.value.replace(/[^0-9]/g, '') }))}
          helperText={t('Federation.wizard.gcpProjectNumberHelp')}
          disabled={!!connection}
          inputProps={{ inputMode: 'numeric' }}
          fullWidth
        />
      )}

      {form.cloud === 'AZURE' && (
        <Alert severity="warning">
          <AlertTitle>{t('Federation.wizard.azureSameTenant.title')}</AlertTitle>
          {t('Federation.wizard.azureSameTenant.body')}
        </Alert>
      )}

      <Alert severity="info">{t('Federation.wizard.noCredentialsNote')}</Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={busy || !!connection}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}
        >
          {t('Federation.wizard.createDraft')}
        </Button>
      </Box>
    </Box>
  );

  const renderArtifact = (title, help, text, filename) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex' }}>
          <Tooltip title={t('Federation.wizard.copy')}>
            <IconButton size="small" onClick={() => copy(text)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Federation.wizard.artifacts.download')}>
            <IconButton size="small" onClick={() => download(filename, text)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {help && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {help}
        </Typography>
      )}
      <Paper
        variant="outlined"
        sx={{ p: 1, mt: 0.5, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto' }}
      >
        {text}
      </Paper>
    </Box>
  );

  // Operator fallback: the client's deployment could not call back (egress blocked,
  // token expired) — bind the principal they report by hand. For GCP it also fills a
  // missing project number after trust.
  const renderManualVerify = (cloud) => (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {gcpNumberMissing ? t('Federation.wizard.manualVerify.gcpNumberMissingTitle') : t('Federation.wizard.manualVerify.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {gcpNumberMissing ? t('Federation.wizard.manualVerify.gcpNumberMissingHelp') : t('Federation.wizard.manualVerify.help')}
          </Typography>
        </Box>
        {!manual.open && (
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setManual((m) => ({
                ...m,
                open: true,
                principal: gcpNumberMissing ? connection?.boundPrincipal || '' : m.principal,
              }))
            }
          >
            {t('Federation.wizard.manualVerify.open')}
          </Button>
        )}
      </Box>
      {manual.open && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
          {template?.manualCallbackCommand && !trustReached && (
            <Box>
              {renderCopyRow(t('Federation.wizard.manualVerify.command'), template.manualCallbackCommand)}
            </Box>
          )}
          <TextField
            size="small"
            label={t(`Federation.wizard.manualVerify.principalLabel.${cloud}`)}
            value={manual.principal}
            onChange={(e) => setManual((m) => ({ ...m, principal: e.target.value }))}
            placeholder={cloud === 'AZURE' ? template?.subject || '' : ''}
            helperText={t(`Federation.wizard.manualVerify.principalHelp.${cloud}`)}
            fullWidth
          />
          {cloud === 'GCP' && (
            <TextField
              size="small"
              label={t('Federation.wizard.gcpProjectNumber')}
              value={manual.projectNumber}
              onChange={(e) => setManual((m) => ({ ...m, projectNumber: e.target.value.replace(/[^0-9]/g, '') }))}
              helperText={t('Federation.wizard.gcpProjectNumberHelp')}
              inputProps={{ inputMode: 'numeric' }}
              fullWidth
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => setManual({ open: false, principal: '', projectNumber: '' })} disabled={busy}>
              {t('Federation.wizard.cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleManualVerify}
              disabled={busy || !manual.principal.trim() || (gcpNumberMissing && !manual.projectNumber.trim())}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}
            >
              {t('Federation.wizard.manualVerify.submit')}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );

  const renderTemplateStep = () => {
    const cloud = connection?.cloud || form.cloud;
    return (
      <Box sx={{ mt: 1 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('Federation.wizard.templateIntro')}
        </Alert>

        {/* Reachability guidance (design §10): federation solves IDENTITY, not network
            reachability. Buckets behind VPC endpoints / IP allow-lists still need our
            egress IPs allow-listed. Surface it here, at onboarding. */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {t('Federation.wizard.reachabilityNote')}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {t('Federation.wizard.reachabilityNoteHelp')}
          </Typography>
        </Alert>

        {busy && !template && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">{t('Federation.wizard.issuingTemplate')}</Typography>
          </Box>
        )}

        {template && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            {template.quickDeployUrl && (
              <Button
                variant="contained"
                href={template.quickDeployUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                sx={{ mb: 2, backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary, opacity: 0.9 } }}
              >
                {t(`Federation.wizard.deployCta.${cloud}`)}
              </Button>
            )}

            {cloud === 'AZURE' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('Federation.wizard.azureSameTenant.templateNote')}
              </Alert>
            )}

            {template.cloudFormationTemplate &&
              renderArtifact(
                t('Federation.wizard.artifacts.cloudFormation'),
                t('Federation.wizard.artifacts.cloudFormationHelp'),
                template.cloudFormationTemplate,
                `wupo-federation-${connection?.id}.yaml`,
              )}
            {template.terraformModule &&
              renderArtifact(
                t('Federation.wizard.artifacts.terraform'),
                t('Federation.wizard.artifacts.terraformHelp'),
                template.terraformModule,
                `wupo-federation-${connection?.id}.tf`,
              )}
            {template.cliScript &&
              renderArtifact(
                t('Federation.wizard.artifacts.azureCli'),
                t('Federation.wizard.artifacts.azureCliHelp'),
                template.cliScript,
                `wupo-federation-${connection?.id}.sh`,
              )}
            {template.armTemplate &&
              renderArtifact(
                t('Federation.wizard.artifacts.armTemplate'),
                t('Federation.wizard.artifacts.armTemplateHelp'),
                template.armTemplate,
                `wupo-federation-${connection?.id}.json`,
              )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              {t('Federation.wizard.trustParams')}
            </Typography>
            {renderCopyRow(t('Federation.wizard.issuer'), template.issuer)}
            {renderCopyRow(t('Federation.wizard.audience'), template.audience)}
            {renderCopyRow(t('Federation.wizard.subject'), template.subject)}
            {renderCopyRow(t('Federation.wizard.callbackUrl'), template.callbackUrl)}
          </Paper>
        )}

        {!template && !trustReached && connection?.onboardingState === 'TEMPLATE_ISSUED' && !busy && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('Federation.wizard.templateAlreadyIssued')}
          </Alert>
        )}

        {/* Waiting on the external HMAC IaC callback to bind the principal. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 1, bgcolor: trustReached ? '#e8f5e9' : '#fff8e1' }}>
          {trustReached ? (
            <CheckCircleIcon color="success" />
          ) : (
            <HourglassEmptyIcon color="warning" />
          )}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {trustReached ? t('Federation.wizard.trustVerified') : t('Federation.wizard.waitingTrust')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {trustReached ? t('Federation.wizard.trustVerifiedHelp') : t('Federation.wizard.waitingTrustHelp')}
            </Typography>
            {!trustReached && tokenExpiresAt && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {t('Federation.wizard.tokenExpires', { date: formatInstant(tokenExpiresAt) })}
              </Typography>
            )}
          </Box>
          {!trustReached && <CircularProgress size={16} sx={{ ml: 'auto' }} />}
        </Box>

        {(!trustReached || gcpNumberMissing) && renderManualVerify(cloud)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button onClick={handleRegenerateTemplate} disabled={busy || trustReached}>
            {template || connection?.onboardingState !== 'TEMPLATE_ISSUED'
              ? t('Federation.wizard.reissueTemplate')
              : t('Federation.wizard.generateTemplate')}
          </Button>
          <Button
            variant="contained"
            onClick={() => setActiveStep(2)}
            disabled={!trustReached}
            sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}
          >
            {t('Federation.wizard.continue')}
          </Button>
        </Box>
      </Box>
    );
  };

  // "Parquet" / "CSV · gz" / "JSON Lines · gz" — format chip of one detected table.
  const formatChipLabel = (tbl) => {
    const label =
      tbl.format === 'JSON' && tbl.jsonLayout
        ? t(`Federation.jsonLayout.${tbl.jsonLayout}`, { defaultValue: 'JSON' })
        : t(`Federation.format.${tbl.format}`, { defaultValue: tbl.format });
    return tbl.compression ? `${label} · ${tbl.compression}` : label;
  };

  const renderDetectedTable = (tbl, i) => (
    <Paper
      key={tbl.tableName || i}
      variant="outlined"
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 2, py: 1, mb: 0.5, borderRadius: '8px' }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
        {tbl.tableName || `table_${i + 1}`}
      </Typography>
      {tbl.format && <Chip size="small" color="primary" variant="outlined" label={formatChipLabel(tbl)} />}
      <Chip size="small" variant="outlined" label={t('Federation.wizard.objectCountShort', { n: tbl.objectCount })} />
      {tbl.dmsLayout && <Chip size="small" color="info" variant="outlined" label={t('Federation.wizard.dms.chip')} />}
      {tbl.partitionColumns && tbl.partitionColumns.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('Federation.wizard.partitionedBy', { columns: tbl.partitionColumns.join(', ') })}
        </Typography>
      )}
      {tbl.latestSnapshot && (
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: { sm: 'auto' } }}>
          {t('Federation.wizard.snapshots', { n: tbl.snapshotCount, latest: formatSnapshotToken(tbl.latestSnapshot) })}
        </Typography>
      )}
    </Paper>
  );

  const renderRejected = (rejected) => (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>{t('Federation.wizard.rejectedTitle', { n: rejected.length })}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {rejected.map((r, i) => (
          <li key={`${r.path}-${r.reason}-${i}`}>
            <Typography variant="body2">
              <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {r.path || t('Federation.wizard.rootPath')}
              </Box>
              {' — '}
              {t(`Federation.rejectReason.${r.reason}`, { defaultValue: r.reason })}
              {' '}({t('Federation.wizard.objectCountShort', { n: r.objectCount })})
            </Typography>
            {r.message && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {r.message}
              </Typography>
            )}
          </li>
        ))}
      </Box>
    </Alert>
  );

  // Layout warnings, localized by code; the backend message carries the specifics
  // (table names, counts) and is the fallback for an unknown code.
  const renderWarnings = (warnings) => (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>{t('Federation.wizard.warningsTitle')}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {warnings.map((w, i) => {
          const label = t(`Federation.layoutWarning.${w.code}`, { defaultValue: w.message || w.code });
          return (
            <li key={`${w.code}-${i}`}>
              <Typography variant="body2">{label}</Typography>
              {w.message && w.message !== label && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {w.message}
                </Typography>
              )}
            </li>
          );
        })}
      </Box>
    </Alert>
  );

  // AWS DMS export(s) detected: DMS CSV has no header row unless AddColumnName=true.
  const renderDmsNote = (dmsTables) => (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>{t('Federation.wizard.dms.title')}</AlertTitle>
      <Typography variant="body2">{t('Federation.wizard.dms.body')}</Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
        {t('Federation.wizard.dms.tables', { tables: dmsTables.map((tbl, i) => tbl.tableName || `table_${i + 1}`).join(', ') })}
      </Typography>
    </Alert>
  );

  // First line of a CSV table's first file, so the user can judge the header switch.
  const renderFirstLine = (tbl, i) => {
    const name = tbl.tableName || `table_${i + 1}`;
    return (
      <Box key={`${name}-first-line`} sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('Federation.wizard.dataOptions.firstLineOf', { table: name })}
          </Typography>
          {tbl.headerDetected === true && (
            <Chip size="small" color="success" variant="outlined" label={t('Federation.wizard.dataOptions.looksLikeHeader')} />
          )}
          {tbl.headerDetected === false && (
            <Chip size="small" color="warning" variant="outlined" label={t('Federation.wizard.dataOptions.looksLikeData')} />
          )}
        </Box>
        <Box
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            whiteSpace: 'pre',
            overflowX: 'auto',
            bgcolor: 'grey.50',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            py: 0.75,
          }}
        >
          {tbl.firstLine}
        </Box>
      </Box>
    );
  };

  // Data options (between Validate and Schedule): shown only when they matter for
  // what the probe found — a dated layout, or any CSV table.
  const renderDataOptions = () => {
    const csvPreviewTables = (validation?.tables || []).filter(
      (tbl) => tbl.format === 'CSV' && typeof tbl.firstLine === 'string',
    );
    const verdicts = new Set(
      (validation?.tables || [])
        .filter((tbl) => tbl.format === 'CSV' && typeof tbl.headerDetected === 'boolean')
        .map((tbl) => tbl.headerDetected),
    );
    const verdictsDisagree = validation?.suggestedCsvHeader == null && verdicts.size > 1;
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('Federation.wizard.dataOptions.title')}
        </Typography>
        {showSnapshotOption && (
          <FormControl sx={{ display: 'block', mb: showCsvHeaderOption ? 2 : 0 }}>
            <FormLabel sx={{ fontSize: '0.85rem' }}>{t('Federation.wizard.dataOptions.snapshotLabel')}</FormLabel>
            <RadioGroup
              value={dataOptions.snapshotMode}
              onChange={(e) => setDataOptions((o) => ({ ...o, snapshotMode: e.target.value }))}
            >
              <FormControlLabel
                value="LATEST"
                disabled={busy}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{t('Federation.wizard.dataOptions.snapshotLatest')}</Typography>}
              />
              <FormControlLabel
                value="ALL"
                disabled={busy}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{t('Federation.wizard.dataOptions.snapshotAll')}</Typography>}
              />
            </RadioGroup>
            {snapshotModeChanged && (
              <FormHelperText sx={{ mx: 0 }}>{t('Federation.wizard.dataOptions.revalidateNote')}</FormHelperText>
            )}
          </FormControl>
        )}
        {showCsvHeaderOption && (
          <Box>
            {csvPreviewTables.map(renderFirstLine)}
            <FormControl sx={{ display: 'block' }}>
              <FormControlLabel
                disabled={busy}
                control={
                  <Switch
                    checked={!!dataOptions.csvHeader}
                    onChange={(e) => {
                      csvHeaderTouchedRef.current = true;
                      setCsvHeaderFromSuggestion(false);
                      const checked = e.target.checked;
                      setDataOptions((o) => ({ ...o, csvHeader: checked }));
                    }}
                  />
                }
                label={<Typography variant="body2">{t('Federation.wizard.dataOptions.csvHeader')}</Typography>}
              />
              <FormHelperText sx={{ mx: 0, mt: 0 }}>{t('Federation.wizard.dataOptions.csvHeaderHelp')}</FormHelperText>
              {csvHeaderFromSuggestion && (
                <FormHelperText sx={{ mx: 0 }}>{t('Federation.wizard.dataOptions.csvHeaderSuggested')}</FormHelperText>
              )}
            </FormControl>
            {verdictsDisagree && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t('Federation.wizard.dataOptions.csvHeaderMixed')}
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const renderValidateStep = () => {
    const rejected = validation?.rejected || [];
    // NO_SUPPORTED_DATA gets its own prominent alert below, not a list entry.
    const warnings = (validation?.warnings || []).filter((w) => w.code !== 'NO_SUPPORTED_DATA');
    const noSupportedData = !!validation && validation.tableCount === 0;
    const dmsTables = (validation?.tables || []).filter((tbl) => tbl.dmsLayout);
    return (
      <Box sx={{ mt: 1 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('Federation.wizard.validateIntro')}
        </Alert>

        {!validation && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleValidate}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <StorageIcon />}
              sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}
            >
              {t('Federation.wizard.runValidation')}
            </Button>
          </Box>
        )}

        {validation && (
          <Box>
            {noSupportedData && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 'bold' }}>{t('Federation.wizard.noSupportedData.title')}</AlertTitle>
                {t('Federation.wizard.noSupportedData.body')}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={t('Federation.wizard.objectCount', { n: validation.objectCount })} />
              <Chip label={t('Federation.wizard.supportedCount', { n: validation.supportedObjectCount })} variant="outlined" />
              <Chip label={t('Federation.wizard.ignoredCount', { n: validation.ignoredObjectCount })} variant="outlined" />
              <Chip
                color={noSupportedData ? 'warning' : 'primary'}
                label={t('Federation.wizard.tableCount', { n: validation.tableCount })}
              />
            </Box>

            {/* The probe reads no data (one readability check per table), so the column
                schema only exists once the first fetch lands the data in Delta. */}
            {!noSupportedData && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                {t('Federation.wizard.schemaAfterFirstFetch')}
              </Typography>
            )}

            {(validation.tables || []).map(renderDetectedTable)}

            {dmsTables.length > 0 && renderDmsNote(dmsTables)}

            {rejected.length > 0 && renderRejected(rejected)}
            {warnings.length > 0 && renderWarnings(warnings)}

            {dataOptions && (showSnapshotOption || showCsvHeaderOption) && renderDataOptions()}

            {optionsApplied && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {t('Federation.wizard.dataOptions.applied')}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button onClick={handleValidate} disabled={busy}>
                {t('Federation.wizard.revalidate')}
              </Button>
              <Button
                variant="contained"
                onClick={handleContinueFromValidate}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}
              >
                {snapshotModeChanged
                  ? t('Federation.wizard.dataOptions.applyAndRevalidate')
                  : t('Federation.wizard.continueToSchedule')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderScheduleStep = () => (
    <Box sx={{ mt: 1 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('Federation.wizard.scheduleIntro')}
      </Alert>
      <SchedulePicker
        initialSpec={connection?.schedule}
        onSubmit={handleSaveSchedule}
        submitting={busy}
        submitLabel={t('Federation.wizard.activate')}
        serverPreview={scheduleResult?.nextRunPreview}
        serverNextRunAt={scheduleResult?.nextRunAt}
      />
      {scheduleResult && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('Federation.wizard.activationEnqueued')}
        </Alert>
      )}
    </Box>
  );

  return (
    <Box>
      {connection && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('Federation.wizard.currentState')}
          </Typography>
          <Chip size="small" color={stateChipColor(connection.onboardingState)} label={t(`Federation.state.${connection.onboardingState}`)} />
          {connection.source && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
              {connection.source}
            </Typography>
          )}
        </Box>
      )}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {activeStep === 0 && renderSourceStep()}
      {activeStep === 1 && renderTemplateStep()}
      {activeStep === 2 && renderValidateStep()}
      {activeStep === 3 && renderScheduleStep()}
    </Box>
  );
}
